import { schluesselVorschlag, type BaumPlugin, type BaumUi, type BaumWorkspace, type StrukturArt } from "@gui-creator/gemeinsam";
import { useQueryClient } from "@tanstack/react-query";
import { BookOpen, ChevronRight, Copy, FilePlus2, FolderPlus, LayoutGrid, MoreHorizontal, Pencil, Plus, Search, Tag, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState, type MouseEvent as ReactMouseEvent, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { schluessel as qk, useStruktur } from "../api/abfragen.ts";
import { api } from "../api/klient.ts";
import { useDarf } from "../huelle/konto.ts";
import { findePfad, uiAdresse } from "../huelle/pfad.ts";
import { useHinweis } from "../ui/Hinweise.tsx";
import { MenueKnopf, MenueListe, type MenuePunkt } from "../ui/Menue.tsx";
import { filtereBaum } from "./filter.ts";
import { InlineEingabe } from "./InlineEingabe.tsx";
import { STATUS_ANZEIGE, StatusMarke } from "./StatusMarke.tsx";
import "./baum.css";

const SPEICHER = "gc-baum-offen";
const ART_PFAD: Record<StrukturArt, string> = { plugin: "plugins", workspace: "workspaces", ui: "uis" };

type Anlegen = { art: StrukturArt; eltern?: string } | null;
type Umbenennen = { art: StrukturArt; id: string; modus: "name" | "schluessel"; anfang: string } | null;

function ladeOffen(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(SPEICHER) ?? "[]") as string[]);
  } catch {
    return new Set();
  }
}

export function Baum({ onNavigiert }: { onNavigiert?: () => void }) {
  const struktur = useStruktur();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const ort = useLocation();
  const hinweis = useHinweis();
  const darfStruktur = useDarf("struktur");
  const [suche, setSuche] = useState("");
  const [offen, setOffen] = useState(ladeOffen);
  const [anlegen, setAnlegen] = useState<Anlegen>(null);
  const [umbenennen, setUmbenennen] = useState<Umbenennen>(null);
  const [kontext, setKontext] = useState<{ x: number; y: number; punkte: MenuePunkt[] } | null>(null);
  const [ziel, setZiel] = useState<string | null>(null);

  const teile = ort.pathname.split("/").filter(Boolean);
  const aktiv = teile[0] === "p" ? findePfad(struktur.data ?? { plugins: [] }, { plugin: teile[1], workspace: teile[2], ui: teile[3] }) : null;

  useEffect(() => {
    if (!aktiv) return;
    const neu = [aktiv.plugin?.id, aktiv.workspace?.id].filter((x): x is string => !!x && !offen.has(x));
    if (neu.length) setOffen((o) => new Set([...o, ...neu]));
  }, [aktiv?.plugin?.id, aktiv?.workspace?.id]);

  useEffect(() => {
    try {
      localStorage.setItem(SPEICHER, JSON.stringify([...offen]));
    } catch {
      // Speicher gesperrt, dann eben ohne Merken.
    }
  }, [offen]);

  const gefiltert = useMemo(() => filtereBaum(struktur.data ?? { plugins: [] }, suche), [struktur.data, suche]);
  const sucht = suche.trim() !== "";
  const istOffen = (id: string) => sucht || offen.has(id);
  const umschalten = (id: string) => setOffen((o) => (o.has(id) ? new Set([...o].filter((x) => x !== id)) : new Set([...o, id])));

  const starteAnlegen = (a: NonNullable<Anlegen>) => {
    if (a.eltern) setOffen((o) => new Set([...o, a.eltern!]));
    setAnlegen(a);
  };

  const neuLaden = () => qc.invalidateQueries({ queryKey: qk.struktur });
  const fehlerZeigen = (e: unknown) => hinweis({ text: (e as Error).message, art: "fehler" });

  const anlegenFertig = async (wert: string) => {
    if (!anlegen) return;
    const s = schluesselVorschlag(wert);
    const name = wert;
    if (anlegen.art === "plugin") await api("/api/plugins", { methode: "POST", body: { schluessel: s, name } });
    if (anlegen.art === "workspace") await api("/api/workspaces", { methode: "POST", body: { plugin: anlegen.eltern, schluessel: s, name } });
    if (anlegen.art === "ui") await api("/api/uis", { methode: "POST", body: { workspace: anlegen.eltern, schluessel: s, ...(wert !== s ? { name } : {}) } });
    if (anlegen.eltern) setOffen((o) => new Set([...o, anlegen.eltern!]));
    setAnlegen(null);
    await neuLaden();
  };

  const umbenennenFertig = async (wert: string) => {
    if (!umbenennen) return;
    const body = umbenennen.modus === "schluessel" ? { schluessel: wert } : { name: wert };
    await api(`/api/${ART_PFAD[umbenennen.art]}/${umbenennen.id}`, { methode: "PATCH", body });
    setUmbenennen(null);
    await neuLaden();
    if (umbenennen.modus === "schluessel" && aktiv) {
      const neu = await qc.fetchQuery({ queryKey: qk.struktur, queryFn: () => api<typeof struktur.data>("/api/struktur") });
      for (const p of neu?.plugins ?? []) for (const w of p.workspaces) for (const u of w.uis) if (u.id === aktiv.ui?.id) navigate(uiAdresse(p.schluessel, w.schluessel, u.schluessel), { replace: true });
    }
  };

  const loeschen = async (art: StrukturArt, id: string, anzeige: string) => {
    try {
      await api(`/api/${ART_PFAD[art]}/${id}`, { methode: "DELETE" });
      if ([aktiv?.plugin?.id, aktiv?.workspace?.id, aktiv?.ui?.id].includes(id)) navigate("/");
      await neuLaden();
      hinweis({
        text: `„${anzeige}“ liegt im Papierkorb.`,
        aktion: {
          text: "Rückgängig",
          ausfuehren: () => {
            void api(`/api/papierkorb/${art}/${id}`, { methode: "POST" }).then(neuLaden, fehlerZeigen);
          },
        },
      });
    } catch (e) {
      fehlerZeigen(e);
    }
  };

  const verschieben = async (uiId: string, workspaceId: string) => {
    try {
      await api(`/api/uis/${uiId}`, { methode: "PATCH", body: { ziel: workspaceId } });
      await neuLaden();
    } catch (e) {
      fehlerZeigen(e);
    }
  };

  const menuePlugin = (p: BaumPlugin): MenuePunkt[] => [
    { text: "Workspace anlegen", symbol: <FolderPlus size={14} />, aktion: () => starteAnlegen({ art: "workspace", eltern: p.id }) },
    { text: "Katalog öffnen", symbol: <BookOpen size={14} />, aktion: () => navigate(`/katalog/${p.schluessel}`) },
    "trenner",
    { text: "Umbenennen", symbol: <Pencil size={14} />, aktion: () => setUmbenennen({ art: "plugin", id: p.id, modus: "name", anfang: p.name }) },
    { text: "Schlüssel ändern", symbol: <Tag size={14} />, aktion: () => setUmbenennen({ art: "plugin", id: p.id, modus: "schluessel", anfang: p.schluessel }) },
    "trenner",
    { text: "In den Papierkorb", symbol: <Trash2 size={14} />, gefahr: true, aktion: () => void loeschen("plugin", p.id, p.name) },
  ];
  const menueWorkspace = (w: BaumWorkspace): MenuePunkt[] => [
    { text: "UI anlegen", symbol: <FilePlus2 size={14} />, aktion: () => starteAnlegen({ art: "ui", eltern: w.id }) },
    "trenner",
    { text: "Umbenennen", symbol: <Pencil size={14} />, aktion: () => setUmbenennen({ art: "workspace", id: w.id, modus: "name", anfang: w.name }) },
    { text: "Schlüssel ändern", symbol: <Tag size={14} />, aktion: () => setUmbenennen({ art: "workspace", id: w.id, modus: "schluessel", anfang: w.schluessel }) },
    "trenner",
    { text: "In den Papierkorb", symbol: <Trash2 size={14} />, gefahr: true, aktion: () => void loeschen("workspace", w.id, w.name) },
  ];
  const menueUi = (u: BaumUi, w: BaumWorkspace): MenuePunkt[] => [
    {
      text: "Duplizieren",
      symbol: <Copy size={14} />,
      aktion: () =>
        void api("/api/uis", { methode: "POST", body: { workspace: w.id, schluessel: `${u.schluessel}_kopie`.slice(0, 48), kopieVon: u.id, ...(u.name ? { name: `${u.name} (Kopie)` } : {}) } }).then(neuLaden, fehlerZeigen),
    },
    "trenner",
    { text: "Umbenennen", symbol: <Pencil size={14} />, aktion: () => setUmbenennen({ art: "ui", id: u.id, modus: "name", anfang: u.name ?? "" }) },
    { text: "Schlüssel ändern", symbol: <Tag size={14} />, aktion: () => setUmbenennen({ art: "ui", id: u.id, modus: "schluessel", anfang: u.schluessel }) },
    "trenner",
    { text: "In den Papierkorb", symbol: <Trash2 size={14} />, gefahr: true, aktion: () => void loeschen("ui", u.id, u.name ?? u.schluessel) },
  ];

  const rechtsklick = (punkte: MenuePunkt[]) => (e: ReactMouseEvent) => {
    if (!darfStruktur) return;
    e.preventDefault();
    setKontext({ x: e.clientX, y: e.clientY, punkte });
  };

  const zeile = (opts: { id: string; art: StrukturArt; tiefe: number; zeichen: ReactNode; text: ReactNode; titel?: string; zu: string; aktivIst: boolean; offen?: boolean; punkte: MenuePunkt[]; nachher?: ReactNode; ziehbar?: { id: string; plugin: string }; ablage?: { id: string; plugin: string } }) => {
    const umbenennenHier = umbenennen?.id === opts.id;
    if (umbenennenHier) {
      return (
        <InlineEingabe
          key={`u-${opts.id}`}
          tiefe={opts.tiefe}
          modus={umbenennen.modus}
          anfang={umbenennen.anfang}
          platzhalter={umbenennen.modus === "schluessel" ? "Neuer Schlüssel" : "Neuer Name"}
          onFertig={umbenennenFertig}
          onAbbrechen={() => setUmbenennen(null)}
        />
      );
    }
    return (
      <div
        className={`baum-zeile${opts.aktivIst ? " baum-aktiv" : ""}${ziel === opts.id ? " baum-ziel" : ""}`}
        style={{ paddingLeft: 4 + opts.tiefe * 14 }}
        onContextMenu={rechtsklick(opts.punkte)}
        onDragOver={(e) => {
          if (!opts.ablage || !e.dataTransfer.types.includes(`application/x-gc-ui-${opts.ablage.plugin}`)) return;
          e.preventDefault();
          setZiel(opts.id);
        }}
        onDragLeave={() => setZiel((z) => (z === opts.id ? null : z))}
        onDrop={(e) => {
          setZiel(null);
          const uiId = opts.ablage ? e.dataTransfer.getData(`application/x-gc-ui-${opts.ablage.plugin}`) : "";
          if (uiId && opts.ablage) {
            e.preventDefault();
            void verschieben(uiId, opts.ablage.id);
          }
        }}
      >
        {opts.offen === undefined ? (
          <span className="baum-luecke" />
        ) : (
          <button type="button" className="baum-klapp" aria-expanded={opts.offen} aria-label={opts.offen ? "Zuklappen" : "Aufklappen"} onClick={() => umschalten(opts.id)}>
            <ChevronRight size={12} className={opts.offen ? "gedreht" : ""} aria-hidden="true" />
          </button>
        )}
        <Link
          to={opts.zu}
          className="baum-text"
          title={opts.titel}
          aria-label={typeof opts.text === "string" ? opts.text : undefined}
          aria-current={opts.aktivIst ? "page" : undefined}
          draggable={!!opts.ziehbar && darfStruktur}
          onDragStart={(e) => {
            if (!opts.ziehbar) return;
            e.dataTransfer.setData(`application/x-gc-ui-${opts.ziehbar.plugin}`, opts.ziehbar.id);
            e.dataTransfer.effectAllowed = "move";
          }}
          onClick={onNavigiert}
        >
          {opts.zeichen}
          <span className="baum-name">{opts.text}</span>
        </Link>
        {opts.nachher}
        {darfStruktur && (
          <MenueKnopf punkte={opts.punkte} label="Weitere Aktionen" className="knopf knopf-still knopf-symbol baum-mehr">
            <MoreHorizontal size={14} aria-hidden="true" />
          </MenueKnopf>
        )}
      </div>
    );
  };

  const anlegenZeile = (art: StrukturArt, eltern: string | undefined, tiefe: number) =>
    anlegen?.art === art && anlegen.eltern === eltern ? (
      <InlineEingabe
        tiefe={tiefe}
        modus="name"
        platzhalter={art === "plugin" ? "Name des Plugins" : art === "workspace" ? "Name des Workspace" : "Schlüssel oder Name der UI"}
        onFertig={anlegenFertig}
        onAbbrechen={() => setAnlegen(null)}
      />
    ) : null;

  return (
    <nav className="baum" aria-label="Struktur">
      <div className="baum-kopf">
        <label className="baum-suche">
          <Search size={13} aria-hidden="true" />
          <input type="search" placeholder="Suchen" aria-label="Baum durchsuchen" value={suche} onChange={(e) => setSuche(e.target.value)} />
        </label>
        {darfStruktur && (
          <button type="button" className="knopf knopf-still knopf-symbol" aria-label="Plugin anlegen" title="Plugin anlegen" onClick={() => setAnlegen({ art: "plugin" })}>
            <Plus size={15} aria-hidden="true" />
          </button>
        )}
      </div>
      <div className="baum-liste">
        {anlegenZeile("plugin", undefined, 0)}
        {struktur.isPending && <p className="baum-leer">Lädt …</p>}
        {struktur.isError && <p className="baum-leer feld-fehler">{struktur.error.message}</p>}
        {struktur.data && struktur.data.plugins.length === 0 && !anlegen && (
          <div className="baum-leer">
            <p>Noch keine Plugins.</p>
            {darfStruktur && (
              <button type="button" className="knopf" onClick={() => setAnlegen({ art: "plugin" })}>
                <Plus size={14} aria-hidden="true" /> Erstes Plugin anlegen
              </button>
            )}
          </div>
        )}
        {sucht && gefiltert.plugins.length === 0 && <p className="baum-leer">Keine Treffer für „{suche.trim()}“.</p>}
        {gefiltert.plugins.map((p) => (
          <div key={p.id} className="baum-gruppe">
            {zeile({ id: p.id, art: "plugin", tiefe: 0, zeichen: null, text: p.name, titel: p.schluessel, zu: `/p/${p.schluessel}`, aktivIst: !aktiv?.workspace && aktiv?.plugin?.id === p.id, offen: istOffen(p.id), punkte: menuePlugin(p) })}
            {istOffen(p.id) && (
              <>
                {p.workspaces.map((w) => (
                  <div key={w.id}>
                    {zeile({
                      id: w.id,
                      art: "workspace",
                      tiefe: 1,
                      zeichen: <LayoutGrid size={13} className="baum-zeichen" aria-hidden="true" />,
                      text: w.name,
                      titel: w.schluessel,
                      zu: `/p/${p.schluessel}/${w.schluessel}`,
                      aktivIst: !aktiv?.ui && aktiv?.workspace?.id === w.id,
                      offen: istOffen(w.id),
                      punkte: menueWorkspace(w),
                      ablage: { id: w.id, plugin: p.id },
                    })}
                    {istOffen(w.id) && (
                      <>
                        {w.uis.map((u) =>
                          zeile({
                            id: u.id,
                            art: "ui",
                            tiefe: 2,
                            zeichen: <StatusMarke status={u.status} />,
                            text: u.name ?? u.schluessel,
                            titel: `${u.schluessel} · ${STATUS_ANZEIGE[u.status].text}`,
                            zu: uiAdresse(p.schluessel, w.schluessel, u.schluessel),
                            aktivIst: aktiv?.ui?.id === u.id,
                            punkte: menueUi(u, w),
                            ziehbar: { id: u.id, plugin: p.id },
                            nachher: u.offeneTodos > 0 ? <span className="baum-zahl zahlen" title={`${u.offeneTodos} offene To-dos`}>{u.offeneTodos}</span> : null,
                          }),
                        )}
                        {anlegenZeile("ui", w.id, 2)}
                        {w.uis.length === 0 && anlegen?.eltern !== w.id && <p className="baum-hinweis" style={{ paddingLeft: 4 + 3 * 14 }}>Leer</p>}
                      </>
                    )}
                  </div>
                ))}
                {anlegenZeile("workspace", p.id, 1)}
              </>
            )}
          </div>
        ))}
      </div>
      {kontext && <MenueListe punkte={kontext.punkte} x={kontext.x} y={kontext.y} label="Aktionen" onSchliessen={() => setKontext(null)} />}
    </nav>
  );
}
